import React, { useState, useEffect } from 'react';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  FileBarChart, Building2, Users, Calendar, Download, 
  FileText, Clock, TrendingUp, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const EscritorioRelatorios = () => {
  const { empresasDisponiveis, loading } = useEmpresa();
  const { api } = useAuth();
  
  const [empresaSelecionada, setEmpresaSelecionada] = useState('todas');
  const [periodoSelecionado, setPeriodoSelecionado] = useState('mes_atual');
  const [gerando, setGerando] = useState(null);

  const relatoriosDisponiveis = [
    {
      id: 'resumo_ponto',
      titulo: 'Resumo de Ponto',
      descricao: 'Visão geral de horas trabalhadas, faltas e extras',
      icone: Clock,
      cor: 'blue'
    },
    {
      id: 'inconsistencias',
      titulo: 'Inconsistências',
      descricao: 'Lista de todas as pendências de ponto por empresa',
      icone: FileText,
      cor: 'amber'
    },
    {
      id: 'banco_horas',
      titulo: 'Banco de Horas',
      descricao: 'Saldo consolidado de banco de horas',
      icone: TrendingUp,
      cor: 'green'
    },
    {
      id: 'empregados',
      titulo: 'Cadastro de Empregados',
      descricao: 'Lista completa de empregados por empresa',
      icone: Users,
      cor: 'purple'
    }
  ];

  const gerarRelatorio = async (tipoRelatorio) => {
    setGerando(tipoRelatorio);
    
    try {
      // Simular geração de relatório
      // Em produção, isso chamaria o endpoint real
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success('Relatório gerado com sucesso!', {
        description: 'O download será iniciado em instantes'
      });
      
      // Aqui seria feito o download do arquivo
      // window.location.href = `/api/relatorios/${tipoRelatorio}?empresa=${empresaSelecionada}&periodo=${periodoSelecionado}`;
      
    } catch (error) {
      toast.error('Erro ao gerar relatório');
      console.error(error);
    } finally {
      setGerando(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="escritorio-relatorios">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Relatórios</h1>
          <p className="text-slate-600 mt-1">
            Gere relatórios consolidados de todas as empresas
          </p>
        </div>
        <Badge variant="outline" className="w-fit">
          <FileBarChart className="w-4 h-4 mr-1" />
          {relatoriosDisponiveis.length} relatórios disponíveis
        </Badge>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configurações do Relatório</CardTitle>
          <CardDescription>
            Selecione a empresa e o período para os relatórios
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Empresa</label>
              <Select value={empresaSelecionada} onValueChange={setEmpresaSelecionada}>
                <SelectTrigger data-testid="select-empresa">
                  <SelectValue placeholder="Selecione uma empresa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Todas as empresas
                    </div>
                  </SelectItem>
                  {empresasDisponiveis.map(empresa => (
                    <SelectItem key={empresa.id} value={empresa.id}>
                      {empresa.nome_fantasia || empresa.razao_social}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Período</label>
              <Select value={periodoSelecionado} onValueChange={setPeriodoSelecionado}>
                <SelectTrigger data-testid="select-periodo">
                  <SelectValue placeholder="Selecione o período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mes_atual">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Mês atual
                    </div>
                  </SelectItem>
                  <SelectItem value="mes_anterior">Mês anterior</SelectItem>
                  <SelectItem value="ultimos_3_meses">Últimos 3 meses</SelectItem>
                  <SelectItem value="ultimos_6_meses">Últimos 6 meses</SelectItem>
                  <SelectItem value="ano_atual">Ano atual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Relatórios */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {relatoriosDisponiveis.map((relatorio) => {
          const IconComponent = relatorio.icone;
          const isGerando = gerando === relatorio.id;
          
          return (
            <Card key={relatorio.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${relatorio.cor}-100`}>
                      <IconComponent className={`w-6 h-6 text-${relatorio.cor}-600`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{relatorio.titulo}</h3>
                      <p className="text-sm text-slate-500 mt-1">{relatorio.descricao}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-xs text-slate-400">
                    {empresaSelecionada === 'todas' 
                      ? `${empresasDisponiveis.length} empresa(s)` 
                      : '1 empresa'}
                  </span>
                  <Button 
                    size="sm"
                    onClick={() => gerarRelatorio(relatorio.id)}
                    disabled={isGerando}
                    data-testid={`btn-gerar-${relatorio.id}`}
                  >
                    {isGerando ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Gerando...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Gerar
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Aviso */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileBarChart className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Relatórios Consolidados</p>
              <p className="text-sm text-blue-700 mt-1">
                Os relatórios são gerados com base nos dados das empresas vinculadas ao seu escritório.
                Ao selecionar "Todas as empresas", o relatório incluirá dados consolidados de todas as empresas.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EscritorioRelatorios;
